import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import type { RootState } from "../redux/store";
import { toggle } from "../redux/navReducer";
import {
  setSelectedOrganisation,
  selectOrganisationId,
  clearSelectedOrganisation,
} from "../redux/organisationReducer";
import {
  Link,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import { adminRoutes } from "../routers/AdminRouters";
import Assets from "../core/assets";
import { setAdminLogout } from "../services/admin/adminReducer";
import {
  useGetOrganisationsQuery,
  useGetProfileQuery,
  useLogoutMutation,
} from "../services";

const Navbar = () => {
  const dispatch = useAppDispatch();
  type NavbarSliceState = { isOpen: boolean };
  const isOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen,
  );
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Get selected organisation id from Redux
  const selectedOrgId = useAppSelector(selectOrganisationId);

  // treat "closed" as collapsed icon-rail
  const isCollapsed = !isOpen;

  // make filled icons green in both themes
  const fillColor = "#B1C223";
  // light theme hover fill is white
  const lightHoverFillColor = "#FFFFFF";

  // Pick logo based on theme
  const displayLogo =
    theme === "light" ? Assets.authLightLogo : Assets.authDarkLogo;

  // type admin selector with RootState
  const { admin, isLoggedIn } = useAppSelector(
    (state: RootState) => state.admin,
  );

  const {
    data: orgsData,
    isLoading: isOrgLoading,
    isError: isOrgError,
    isFetching: isOrgFetching,
  } = useGetOrganisationsQuery(undefined, {
    skip: !isLoggedIn,
    refetchOnMountOrArgChange: true,
  });

  const {
    data: profileResp,
    refetch,
    isFetching: isProfileFetching,
  } = useGetProfileQuery(undefined, {
    skip: !isLoggedIn,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (isLoggedIn) refetch();
  }, [isLoggedIn, refetch]);

  const profile = admin ?? profileResp?.result ?? profileResp;

  const rawRole =
    (profile as any)?.role ??
    (profile as any)?.data?.role ??
    (profile as any)?.user?.role;

  const isSuperAdmin =
    typeof rawRole === "string" &&
    rawRole.toLowerCase().replace(/[\s_-]+/g, "") === "superadmin";

  const isAdmin =
    typeof rawRole === "string" &&
    rawRole.toLowerCase().replace(/[\s_-]+/g, "") === "admin";

  const organisationOptions: Array<any> = isLoggedIn
    ? isSuperAdmin
      ? ((orgsData as any)?.data ?? (orgsData as any) ?? [])
      : isAdmin
        ? ((profile as any)?.assignedOrganisations ?? [])
        : []
    : [];

  const selectedOrgObj = organisationOptions.find(
    (org: any) => String(org?._id) === selectedOrgId,
  );

  const paymentProvider =
    selectedOrgObj?.gateway?.provider?.toLowerCase() || "stripe";
  const isRazorpay = paymentProvider === "razorpay";
  const isPremiumOrg = selectedOrgObj?.level?.toUpperCase() === "PREMIUM";
  // Sync URL param to Redux
  useEffect(() => {
    const orgIdFromUrl = searchParams.get("orgId");
    if (orgIdFromUrl && orgIdFromUrl !== selectedOrgId) {
      dispatch(setSelectedOrganisation(orgIdFromUrl));
    }
  }, [searchParams, selectedOrgId, dispatch]);

  // Auto-select first organisation if none selected (neither in URL nor Redux)
  useEffect(() => {
    if (isLoggedIn && !isProfileFetching && !isOrgFetching) {
      const orgIdFromUrl = searchParams.get("orgId");
      if (!selectedOrgId && !orgIdFromUrl && organisationOptions.length > 0) {
        const firstOrgId = organisationOptions[0]._id;
        dispatch(setSelectedOrganisation(firstOrgId));
        setSearchParams((prev) => {
          prev.set("orgId", firstOrgId);
          return prev;
        });
      } else if (selectedOrgId && !orgIdFromUrl) {
        // If we have a selectedOrgId in Redux (e.g. from sessionStorage) but not in URL, update URL
        setSearchParams((prev) => {
          prev.set("orgId", selectedOrgId);
          return prev;
        });
      }
    }
  }, [
    organisationOptions,
    selectedOrgId,
    dispatch,
    isLoggedIn,
    isProfileFetching,
    isOrgFetching,
    searchParams,
    setSearchParams,
  ]);

  const MaskedIcon: React.FC<{
    src: string;
    filled: boolean;
    filledSrc?: string;
    className?: string;
    alt?: string;
  }> = ({ src, filled, filledSrc, className }) => {
    const maskUrl = filled && filledSrc ? filledSrc : src;

    // Exact color logic (no filters)
    // - Light: default green, hover/active white
    // - Dark: default gray, hover/active green
    const defaultDark = "#9CA3AF";
    const currentFillColor =
      theme === "light"
        ? filled
          ? lightHoverFillColor
          : fillColor
        : filled
          ? fillColor
          : defaultDark;

    return (
      <div className={`relative w-5 h-5 ${className || ""}`}>
        <span
          className="absolute inset-0 block transition-colors duration-200"
          style={{
            backgroundColor: currentFillColor,
            // Standard mask
            maskImage: `url(${maskUrl})`,
            maskRepeat: "no-repeat",
            maskPosition: "center",
            maskSize: "contain",
            // WebKit mask
            WebkitMaskImage: `url(${maskUrl})`,
            WebkitMaskRepeat: "no-repeat",
            WebkitMaskPosition: "center",
            WebkitMaskSize: "contain",
          }}
          aria-hidden="true"
        />
      </div>
    );
  };

  const handleToggle = () => {
    dispatch(toggle());
  };

  const handleCloseSidebar = () => {
    if (isOpen) dispatch(toggle());
  };

  const goTo = (path: string) => {
    navigate(path);
    handleCloseSidebar();
  };

  const [logoutApi] = useLogoutMutation();

  const handleLogout = async () => {
    try {
      await logoutApi().unwrap();
    } catch (error) {
      console.error("Logout failed", error);
    }
    dispatch(setAdminLogout());
    dispatch(clearSelectedOrganisation());
    navigate(adminRoutes.login);
  };

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  const isAdminActive = location.pathname.startsWith(
    `${adminRoutes.adminList}`,
  );
  const showAdminSub = isOpen && (adminMenuOpen || hoveredItem === "admin");

  const isOrganisationActive = location.pathname.startsWith(
    `${adminRoutes.organisationList}`,
  );
  const showOrganisationSub =
    isOpen && (isOrganisationActive || hoveredItem === "organisation");

  const isGymActive = location.pathname.startsWith(`${adminRoutes.gymList}`);
  const showGymSub = isOpen && (isGymActive || hoveredItem === "gym");

  const isStaffActive = location.pathname.startsWith(
    `${adminRoutes.staffList}`,
  );
  const showStaffSub = isOpen && (isStaffActive || hoveredItem === "staff");

  const isSubscriptionsActive = location.pathname.startsWith(
    `${adminRoutes.licenses}`,
  );
  const showSubscriptionsSub =
    isOpen && (isSubscriptionsActive || hoveredItem === "subscriptions");

  const isWorkoutActive = location.pathname.startsWith(
    `${adminRoutes.exerciseList}`,
  );
  const showWorkoutSub =
    isOpen && (isWorkoutActive || hoveredItem === "workout");

  // ADDED: Settings submenu logic
  const isSettingsActive =
    location.pathname.startsWith("/settings") ||
    location.pathname.startsWith("/support");
  const showSettingsSub =
    isOpen && (isSettingsActive || hoveredItem === "settings");

  // Label helper: inline when expanded, tooltip when collapsed
  const SideLabel: React.FC<{ text: string; className?: string }> = ({
    text,
    className,
  }) =>
    isOpen ? (
      <span className={`flex-1 whitespace-nowrap ${className || ""}`}>
        {text}
      </span>
    ) : (
      <span
        className={`absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 rounded-md text-xs shadow whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none ${
          theme === "dark"
            ? "bg-gray-800 text-white"
            : "bg-white text-gray-900 border border-gray-200"
        } ${className || ""}`}
      >
        {text}
      </span>
    );

  // theme-aware divider for section separation (used when collapsed)
  const Divider: React.FC = () => (
    <div
      className={`${isOpen ? "mx-3" : "mx-1"} my-2 border-b ${
        theme === "dark" ? "border-gray-800" : "border-gray-200"
      }`}
    />
  );

  return (
    <>
      {/* Main Navbar */}
      <nav
        className={`w-full h-16 flex items-center justify-between px-6 fixed top-0 z-50 border-b transition-colors duration-200 ${
          theme === "dark"
            ? "bg-[#1a1a1a] border-gray-700 text-white"
            : "bg-white border-gray-200 text-gray-900"
        }`}
      >
        {/* Left Section */}
        <div className="flex items-center space-x-4">
          <Link to={adminRoutes.dashboard}>
            <button
              type="button"
              onClick={handleToggle}
              title="Toggle sidebar"
              className="flex items-center space-x-2 cursor-pointer focus:outline-none"
              aria-expanded={isOpen}
              aria-controls="app-sidebar"
            >
              {isOpen ? (
                <img src={displayLogo} alt="Gym Stream" className="h-8 w-25" />
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="28"
                  viewBox="0 0 40 28"
                  fill="none"
                  aria-label="Gym Stream"
                >
                  <path
                    d="M13.1671 0.089942C12.4542 0.089942 11.7791 0.194084 11.1465 0.407102C10.5138 0.62012 9.9237 0.923078 9.38078 1.32071C8.83785 1.71834 8.37519 2.19645 7.99278 2.7503C7.61037 3.30888 7.31294 3.929 7.1005 4.62012L2.88458 18.4473L2.25667 20.497H27.7033L27.5427 21.0935C27.4106 21.4627 27.1839 21.7704 26.8676 22.0071C26.5513 22.2438 26.2067 22.3669 25.8384 22.3669H4.31978C3.55497 22.3669 2.85153 22.5988 2.2189 23.0627C1.58628 23.5266 1.14722 24.142 0.911167 24.9089L0 27.929H26.32C27.7222 27.929 28.978 27.5172 30.0874 26.6982C31.1969 25.8793 31.9617 24.7905 32.3866 23.4367L34.9643 14.9302H34.9596L36.6733 9.30651L37.2115 7.5361L37.2304 7.47929H18.601C18.3886 7.47929 18.1809 7.50296 17.9826 7.5361C17.4538 7.63077 16.9628 7.83906 16.5002 8.17515C15.8675 8.63906 15.4285 9.25444 15.1924 10.0213L14.2813 13.0414H29.9788L29.9599 13.0982L29.4217 14.9065H9.47992L11.7508 7.53136L11.9396 6.91598C12.0718 6.54675 12.289 6.23905 12.5958 6.00237C12.898 5.76568 13.2521 5.6426 13.6486 5.6426H35.1342C35.9274 5.6426 36.6403 5.42012 37.2729 4.96568C37.9055 4.51598 38.3304 3.90533 38.5429 3.13846L39.454 0.0757408H13.1671V0.089942Z"
                    fill="#C6D92C"
                  />
                </svg>
              )}
            </button>
          </Link>
        </div>

        {/* Right Section */}
        <div className="flex items-center space-x-4">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className={`relative w-20 h-8 rounded-full transition-all duration-300 flex items-center ${
              theme === "dark"
                ? "bg-gray-700 border border-gray-600"
                : "bg-gray-200 border border-gray-300"
            }`}
          >
            <div
              className={`absolute w-6 h-6 rounded-full transition-all duration-300 flex items-center justify-center ${
                theme === "dark"
                  ? "translate-x-12 bg-gray-800"
                  : "translate-x-1 bg-[#B6CB24]"
              }`}
            >
              {theme === "dark" ? (
                <img
                  src={Assets.darkModeIcon}
                  alt="Dark Mode"
                  className="w-4 h-4"
                />
              ) : (
                <svg
                  className="w-3 h-3 text-gray-800"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414-1.414zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
            <span
              className={`absolute left-2 text-xs font-medium transition-opacity duration-300 ${
                theme === "dark" ? "opacity-100 text-gray-300" : "opacity-0"
              }`}
            >
              DARK
            </span>
            <span
              className={`absolute right-2 text-xs font-medium transition-opacity duration-300 ${
                theme === "light" ? "opacity-100 text-gray-600" : "opacity-0"
              }`}
            >
              LIGHT
            </span>
          </button>

          {/* Organisation Dropdown - only for SuperAdmin */}
          {(isSuperAdmin || isAdmin) && (
            <div className="relative">
              <select
                value={selectedOrgId}
                onChange={(e) => {
                  const newOrgId = e.target.value;
                  dispatch(setSelectedOrganisation(newOrgId));
                  setSearchParams((prev) => {
                    prev.set("orgId", newOrgId);
                    return prev;
                  });
                }}
                className={`appearance-none px-3 py-2 rounded-lg border transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#B6CB24] ${
                  theme === "dark"
                    ? "bg-gray-800 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                <option value="" disabled>
                  {organisationOptions.length > 0
                    ? "Choose Organisation"
                    : "No organisations"}
                </option>
                {organisationOptions.map((o) => (
                  <option key={o?._id} value={o?._id}>
                    {o.displayName ? o.displayName : o.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </nav>

      {/* Sidebar */}
      <div
        id="app-sidebar"
        className={`fixed left-0 top-16 h-[calc(100vh-4rem)] ${
          isOpen ? "w-64" : "w-16"
        } shadow-lg transition-[width] duration-300 ease-in-out z-40 ${
          theme === "dark"
            ? "bg-[#1a1a1a] border-gray-700 text-white"
            : "bg-white border-gray-200 text-gray-900"
        }`}
      >
        <div className="py-4 h-full flex flex-col">
          {/* Navigation Links */}
          <div
            className={`flex-1 space-y-px overflow-y-auto overflow-x-hidden ${isOpen ? "px-3" : "px-1"}`}
          >
            <Link
              to="/"
              className={`group relative flex items-center ${
                isOpen ? "px-3" : "px-2 justify-center"
              } py-2 rounded-lg font-medium transition-all duration-200 ${
                hoveredItem === "dashboard"
                  ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-[#B6CB24]"
                  : theme === "dark"
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-700 hover:text-gray-900"
              }`}
              onMouseEnter={() => setHoveredItem("dashboard")}
              onMouseLeave={() => setHoveredItem(null)}
              onClick={() => {
                handleToggle();
              }}
            >
              <MaskedIcon
                src={Assets.dashboardIcon}
                filled={hoveredItem === "dashboard"}
                filledSrc={Assets.dashboardIconFilled}
                className={`${isOpen ? "mr-3" : ""}`}
                alt="Dashboard"
              />
              <SideLabel text="Dashboard" />
            </Link>

            <div
              className="flex flex-col"
              onMouseEnter={() => setHoveredItem("subscriptions")}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <button
                type="button"
                className={`group relative flex items-center w-full text-left ${
                  isOpen ? "px-3" : "px-2 justify-center"
                } py-2 rounded-lg font-medium transition-all duration-200 ${
                  isSubscriptionsActive || hoveredItem === "subscriptions"
                    ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-[#B6CB24]"
                    : theme === "dark"
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-700 hover:text-gray-900"
                }`}
                onClick={() => {
                  goTo(adminRoutes.licenses);
                  handleToggle();
                }}
                aria-expanded={showSubscriptionsSub}
                aria-controls="subscriptions-submenu"
              >
                <MaskedIcon
                  src={Assets.subscriptionsIcon}
                  filled={
                    hoveredItem === "subscriptions" || isSubscriptionsActive
                  }
                  filledSrc={Assets.subscriptionsIconFilled}
                  className={`${isOpen ? "mr-3 " : ""}`}
                  alt="Subscriptions"
                />
                <SideLabel text="Subscriptions" />
              </button>

              <div
                id="subscriptions-submenu"
                className={`overflow-hidden transition-[max-height,opacity] duration-200 ${
                  showSubscriptionsSub
                    ? "max-h-32 opacity-100"
                    : "max-h-0 opacity-0"
                }`}
              >
                <Link
                  to={adminRoutes.licenses}
                  onClick={() => {
                    handleCloseSidebar();
                    handleToggle();
                  }}
                  className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActiveRoute("/screens")
                      ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                      : theme === "dark"
                        ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  Licenses
                </Link>
                <Link
                  to="billing"
                  onClick={() => {
                    handleCloseSidebar();
                    handleToggle();
                  }}
                  className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActiveRoute("/billing")
                      ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                      : theme === "dark"
                        ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  Billing
                </Link>
                {!isRazorpay && (
                  <Link
                    to={adminRoutes.paymentList}
                    onClick={() => {
                      handleCloseSidebar();
                      handleToggle();
                    }}
                    className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActiveRoute("/payment-details")
                        ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                        : theme === "dark"
                          ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                          : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    Payment Details
                  </Link>
                )}
              </div>
            </div>

            {/* Management Section - only when expanded */}
            {isOpen && (
              <div
                className={`px-3 py-1 text-xs font-semibold uppercase tracking-wider ${
                  theme === "dark" ? "text-gray-500" : "text-gray-400"
                } border-b ${
                  theme === "dark" ? "border-gray-800" : "border-gray-200"
                } mb-2 mt-2`}
              >
                Management
              </div>
            )}
            {isCollapsed && <Divider />}
            {isSuperAdmin && (
              <div
                className="flex flex-col"
                onMouseEnter={() => setHoveredItem("admin")}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <button
                  type="button"
                  className={`group relative flex items-center w-full text-left ${
                    isOpen ? "px-3" : "px-2 justify-center"
                  } py-2 rounded-lg font-medium transition-all duration-200 ${
                    isAdminActive || hoveredItem === "admin"
                      ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-[#B6CB24]"
                      : theme === "dark"
                        ? "text-gray-300 hover:text-white"
                        : "text-gray-700 hover:text-gray-900"
                  }`}
                  onClick={() => {
                    goTo("/admin");
                    handleToggle();
                  }}
                  aria-expanded={showAdminSub}
                  aria-controls="admin-submenu"
                >
                  <MaskedIcon
                    src={Assets.adminIcon}
                    filled={isAdminActive || hoveredItem === "admin"}
                    filledSrc={Assets.adminIconFilled}
                    className={`${isOpen ? "mr-3" : ""}`}
                    alt="Admin"
                  />
                  <SideLabel text="Admin" />
                </button>

                {/* Submenu: List (only when expanded) */}
                <div
                  id="admin-submenu"
                  className={`overflow-hidden transition-[max-height,opacity] duration-200 ${
                    showAdminSub ? "max-h-24 opacity-100" : "max-h-0 opacity-0"
                  }`}
                >
                  <Link
                    to="/admin"
                    onClick={() => {
                      handleCloseSidebar();
                      handleToggle();
                    }}
                    className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActiveRoute("/admin")
                        ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                        : theme === "dark"
                          ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                          : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                  >
                    List
                  </Link>
                </div>
              </div>
            )}

            <div
              className="flex flex-col"
              onMouseEnter={() => setHoveredItem("organisation")}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <button
                type="button"
                className={`group relative flex items-center w-full text-left ${
                  isOpen ? "px-3" : "px-2 justify-center"
                } py-2 rounded-lg font-medium transition-all duration-200 ${
                  isOrganisationActive || hoveredItem === "organisation"
                    ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-[#B6CB24]"
                    : theme === "dark"
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-700 hover:text-gray-900"
                }`}
                onClick={() => {
                  goTo("/organisation");
                  handleToggle();
                }}
                aria-expanded={showOrganisationSub}
                aria-controls="organisation-submenu"
              >
                <MaskedIcon
                  src={Assets.organisationIcon}
                  filled={
                    hoveredItem === "organisation" || isOrganisationActive
                  }
                  filledSrc={Assets.organisationFilled}
                  className={`${isOpen ? "mr-3" : ""}`}
                  alt="Organisation"
                />
                <SideLabel text="Organisation" />
              </button>

              {/* Submenu stays visible during hover */}
              <div
                id="organisation-submenu"
                className={`overflow-hidden transition-[max-height,opacity] duration-200 ${
                  showOrganisationSub
                    ? "max-h-40 opacity-100"
                    : "max-h-0 opacity-0"
                }`}
              >
                <Link
                  to="/organisation"
                  onClick={() => {
                    handleCloseSidebar();
                    handleToggle();
                  }}
                  className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActiveRoute("/organisation")
                      ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                      : theme === "dark"
                        ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  List
                </Link>
              </div>
            </div>

            {/* Gym */}
            <div
              className="flex flex-col"
              onMouseEnter={() => setHoveredItem("gym")}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <button
                type="button"
                className={`group relative flex items-center w-full text-left ${
                  isOpen ? "px-3" : "px-2 justify-center"
                } py-2 rounded-lg font-medium transition-all duration-200 ${
                  isGymActive || hoveredItem === "gym"
                    ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-[#B6CB24]"
                    : theme === "dark"
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-700 hover:text-gray-900"
                }`}
                onClick={() => {
                  goTo("/gym");
                  handleToggle();
                }}
                aria-expanded={showGymSub}
                aria-controls="gym-submenu"
              >
                <MaskedIcon
                  src={Assets.gymIcon}
                  filled={hoveredItem === "gym" || isGymActive}
                  filledSrc={Assets.gymIconFilled}
                  className={`${isOpen ? "mr-3" : ""}`}
                  alt="Gym"
                />
                <SideLabel text="Gym" />
              </button>

              {/* Submenu (only when expanded) */}
              <div
                id="gym-submenu"
                className={`overflow-hidden transition-[max-height,opacity] duration-200 ${
                  showGymSub ? "max-h-40 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <Link
                  to="/gym"
                  onClick={() => {
                    handleCloseSidebar();
                    handleToggle();
                  }}
                  className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActiveRoute("/gym")
                      ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                      : theme === "dark"
                        ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  List
                </Link>
              </div>
            </div>

            <div
              className="flex flex-col"
              onMouseEnter={() => setHoveredItem("staff")}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <button
                type="button"
                className={`group relative flex items-center w-full text-left ${
                  isOpen ? "px-3" : "px-2 justify-center"
                } py-2 rounded-lg font-medium transition-all duration-200 ${
                  isStaffActive || hoveredItem === "staff"
                    ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-[#B6CB24]"
                    : theme === "dark"
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-700 hover:text-gray-900"
                }`}
                onClick={() => {
                  goTo("/staff");
                  handleToggle();
                }}
                aria-expanded={showStaffSub}
                aria-controls="staff-submenu"
              >
                <MaskedIcon
                  src={Assets.staffIcon}
                  filled={hoveredItem === "staff" || isStaffActive}
                  filledSrc={Assets.staffIconFilled}
                  className={`${isOpen ? "mr-3" : ""}`}
                  alt="Staff"
                />
                <SideLabel text="Staff" />
              </button>

              <div
                id="staff-submenu"
                className={`overflow-hidden transition-[max-height,opacity] duration-200 ${
                  showStaffSub ? "max-h-40 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <Link
                  to="/staff"
                  onClick={() => {
                    handleCloseSidebar();
                    handleToggle();
                  }}
                  className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActiveRoute("/staff")
                      ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                      : theme === "dark"
                        ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  List
                </Link>
              </div>
            </div>

            {/* Workout */}
            <div
              className="flex flex-col"
              onMouseEnter={() => setHoveredItem("workout")}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <button
                type="button"
                className={`group relative flex items-center w-full text-left ${
                  isOpen ? "px-3" : "px-2 justify-center"
                } py-2 rounded-lg font-medium transition-all duration-200 ${
                  isWorkoutActive || hoveredItem === "workout"
                    ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-[#B6CB24]"
                    : theme === "dark"
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-700 hover:text-gray-900"
                }`}
                onClick={() => {
                  goTo("/exercise");
                  handleToggle();
                }}
                aria-expanded={showWorkoutSub}
                aria-controls="workout-submenu"
              >
                <MaskedIcon
                  src={Assets.workoutIcon}
                  filled={hoveredItem === "workout" || isWorkoutActive}
                  filledSrc={Assets.workoutIconFilled}
                  className={`${isOpen ? "mr-3" : ""}`}
                  alt="Workout"
                />
                <SideLabel text="Workout" />
              </button>

              {/* Workout Submenu */}
              <div
                id="workout-submenu"
                className={`overflow-hidden transition-[max-height,opacity] duration-200 ${
                  showWorkoutSub ? "max-h-32 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <Link
                  to="/exercise"
                  onClick={() => {
                    handleCloseSidebar();
                    handleToggle();
                  }}
                  className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActiveRoute("/exercise")
                      ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                      : theme === "dark"
                        ? "text-[#B1C223] hover:bg-gray-800 hover:text-white"
                        : "text-[#B1C223] hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  Exercises
                </Link>
                <Link
                  to={adminRoutes.sessionList}
                  onClick={() => {
                    handleCloseSidebar();
                    handleToggle();
                  }}
                  className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActiveRoute(`${adminRoutes.sessionList}`)
                      ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                      : theme === "dark"
                        ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  Sessions
                </Link>
                <Link
                  to={adminRoutes.schedule}
                  onClick={() => {
                    handleCloseSidebar();
                    handleToggle();
                  }}
                  className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActiveRoute(`${adminRoutes.schedule}`)
                      ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                      : theme === "dark"
                        ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  Schedule
                </Link>
              </div>
            </div>

            {/* Devices */}
            <Link
              to={adminRoutes.deviceList}
              className={`group relative flex items-center ${
                isOpen ? "px-3" : "px-2 justify-center"
              } py-2 rounded-lg font-medium transition-all duration-200 ${
                hoveredItem === "devices"
                  ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-[#B6CB24]"
                  : theme === "dark"
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-700 hover:text-gray-900"
              }`}
              onMouseEnter={() => setHoveredItem("devices")}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <MaskedIcon
                src={Assets.devicesIcon}
                filled={hoveredItem === "devices"}
                filledSrc={Assets.devicesIconFilled}
                className={`${isOpen ? "mr-3" : ""}`}
                alt="Devices"
              />
              <SideLabel text="Devices" />
            </Link>

            {/* Settings Main Dropdown */}
            <div
              className="flex flex-col"
              onMouseEnter={() => setHoveredItem("settings")}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <button
                type="button"
                className={`group relative flex items-center w-full text-left ${
                  isOpen ? "px-3" : "px-2 justify-center"
                } py-2 rounded-lg font-medium transition-all duration-200 ${
                  isSettingsActive || hoveredItem === "settings"
                    ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-[#B6CB24]"
                    : theme === "dark"
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-700 hover:text-gray-900"
                }`}
                onClick={() => {
                  if (!isOpen) handleToggle();
                }}
                aria-expanded={showSettingsSub}
                aria-controls="settings-submenu"
              >
                <MaskedIcon
                  src={Assets.settingsIcon}
                  filled={hoveredItem === "settings" || isSettingsActive}
                  filledSrc={Assets.settingsIconFilled || Assets.settingsIcon}
                  className={`${isOpen ? "mr-3" : ""}`}
                  alt="Settings"
                />
                <SideLabel text="Settings" />
              </button>

              {/* Settings Submenu */}
              <div
                id="settings-submenu"
                className={`overflow-hidden transition-[max-height,opacity] duration-200 ${
                  showSettingsSub ? "max-h-32 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <Link
                  to={adminRoutes.themeSettings}
                  onClick={() => {
                    handleCloseSidebar();
                    handleToggle();
                  }}
                  className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActiveRoute("/settings/theme")
                      ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                      : theme === "dark"
                        ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                >
                  Theme Settings
                </Link>
                {/* <Link
                  to="/settings/support"
                  onClick={() => {
                    handleCloseSidebar();
                    handleToggle();
                  }}
                  className={`mt-1 ml-8 flex items-center px-3 py-2 rounded-lg text-sm transition-colors ${isActiveRoute("/settings/support")
                    ? "bg-[#B6CB24]/20 text-[#B6CB24] border border-none"
                    : theme === "dark"
                      ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                    }`}
                >
                  Support Center
                </Link> */}
              </div>
            </div>
          </div>

          {/* Logout section pinned to bottom with top border */}
          <div
            className={`mt-auto ${
              isOpen ? "px-3" : "px-1"
            } pt-3 pb-1 border-t ${
              theme === "dark" ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <button
              onClick={handleLogout}
              className={`group relative flex items-center w-full ${
                isOpen ? "px-3" : "px-2 justify-center"
              } py-1 rounded-lg font-medium transition-colors duration-200 ${
                theme === "dark"
                  ? "text-gray-300 hover:text-white"
                  : "text-gray-700 hover:text-gray-900"
              }`}
              onMouseEnter={() => setHoveredItem("logout")}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <MaskedIcon
                src={Assets.logoutIcon}
                filled={hoveredItem === "logout"}
                className={`${isOpen ? "mr-2" : ""}`}
                alt="Logout"
              />
              {isOpen ? (
                <span className="whitespace-nowrap">Logout</span>
              ) : (
                <span
                  className={`absolute left-full top-1/2 -translate-y-1/2 ml-1 px-2 py-1 rounded-md text-xs shadow whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none ${
                    theme === "dark"
                      ? "bg-gray-800 text-white"
                      : "bg-white text-gray-900 border border-gray-200"
                  }`}
                >
                  Logout
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Overlay to close sidebar (keep only when expanded) */}
      {isOpen && (
        <div
          className="fixed top-16 inset-x-0 bottom-0 bg-black/50 z-30"
          onClick={handleToggle}
        />
      )}
    </>
  );
};

export default Navbar;
